import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { FaShoppingCart, FaCheckCircle } from 'react-icons/fa';
import { db } from '../firebase'; 
import { doc, getDoc } from 'firebase/firestore'; 

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0, 
    }).format(amount);
};

const ProductDetail = () => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const { addToCart } = useStore();
    
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quantity, setQuantity] = useState(1);
    
    useEffect(() => {
        const fetchProduct = async () => {
            if (!id) {
                setLoading(false);
                setError("No product ID provided in the URL.");
                return;
            }

            try {
                const productDocRef = doc(db, 'products', id);
                const docSnap = await getDoc(productDocRef);

                if (docSnap.exists()) {
                    setProduct({ id: docSnap.id, ...docSnap.data() });
                } else {
                    setError("Product not found. Check the document ID in Firestore.");
                }
                
            } catch (err) {
                console.error("Error fetching single product:", err);
                setError("Failed to load product details due to a database error.");
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id]); 


    const handleAddToCart = () => {
        if (product) {
            addToCart(product, quantity); 
            // navigate('/cart'); // Removed navigation to cart

        }
    };
    
    const productStock = product?.stock || 0; 
    const isOutOfStock = productStock <= 0;

    if (loading) {
        return <div className="text-center py-20 text-lg font-semibold">Loading product details...</div>;
    }

    if (error) {
        return <div className="text-center py-20 text-lg text-red-600 font-semibold">Error: {error}</div>;
    }

    if (!product) {
        return <div className="text-center py-20 text-lg text-gray-500">Product not found.</div>;
    }

    return (
        <div className="bg-gray-100 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row -mx-4">
                    <div className="md:flex-1 px-4">
                        <div className="h-[460px] rounded-lg bg-gray-300 mb-4">
                            <img className="w-full h-full object-contain" src={product.imageUrl || '/placeholder.png'} alt={product.name} />
                        </div>
                    </div>
                    <div className="md:flex-1 px-4">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">{product.name}</h2>
                        <p className="text-gray-600 text-sm mb-4">
                            Brand: <span className="font-semibold">{product.brand}</span>
                        </p>
                        <div className="flex mb-4">
                            <div className="mr-4">
                                <span className="font-bold text-gray-700">Price:</span>
                                <span className="text-red-600 text-2xl font-bold ml-2">{formatCurrency(product.price)}</span>
                            </div>
                            <div>
                                <span className="font-bold text-gray-700">Availability:</span>
                                <span className={`ml-2 ${isOutOfStock ? 'text-red-600' : 'text-green-600'}`}>
                                    {isOutOfStock ? 'Out of Stock' : 'In Stock'}
                                </span>
                            </div>
                        </div>
                        <div className="mb-4">
                            <span className="font-bold text-gray-700">Product Description:</span>
                            <p className="text-gray-600 text-sm mt-2">{product.description}</p>
                        </div>
                        {product.keySpecs && product.keySpecs.length > 0 && (
                            <div className="mb-4">
                                <span className="font-bold text-gray-700">Key Specifications:</span>
                                <ul className="list-disc list-inside text-gray-600 text-sm mt-2">
                                    {product.keySpecs.map((spec, index) => (
                                        <li key={index}>{spec}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="flex -mx-2 mb-4">
                            <div className="w-1/2 px-2">
                                <label htmlFor="quantity" className="font-bold text-gray-700">Quantity:</label>
                                <input
                                    id="quantity"
                                    type="number"
                                    min="1"
                                    max={productStock || 1}
                                    value={quantity}
                                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                    className="w-full text-center border border-gray-300 rounded-md py-2 px-3"
                                    disabled={isOutOfStock}
                                />
                            </div>
                            <div className="w-1/2 px-2">
                                <button 
                                    onClick={handleAddToCart}
                                    disabled={isOutOfStock}
                                    className="w-full bg-yellow-400 text-blue-900 py-2 px-4 rounded-full font-bold hover:bg-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                                >
                                    <FaShoppingCart className="inline-block mr-2" />
                                    {isOutOfStock ? 'SOLD OUT' : 'Add to Cart'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;